/*
  Nature Lane Beauty — product page behaviour.

  The gallery moved to nlb-product-gallery.js on 2026-08-10, when the routine
  page became the gallery snippet's second consumer. What is left here is the
  part that is genuinely product-page-only: it binds by .nlb-product__form and
  owns the PDP sticky bar.

  Two things:
    - custom build #8, the purchase-option selector's price display and
      interval select
    - custom build #2, the sticky mobile add-to-cart bar

  Everything here is enhancement. The radios are `name="selling_plan"` and the
  quantity input is `name="quantity"`, so the form posts correctly with this
  script switched off — a one-time purchase, or a subscription at the default
  interval. The only control that depends on JS is the interval select, which
  is why it ships `hidden` and is revealed here rather than the other way
  round: a select that appears to work and silently doesn't is worse than one
  that isn't offered.
*/
(function () {
  'use strict';

  /* ---- Purchase options — custom build #8 -------------------------------- */

  class NlbPurchaseOptions {
    constructor(form) {
      this.form = form;
      this.fieldset = form.querySelector('[data-nlb-purchase]');
      this.plans = form.querySelectorAll('[data-nlb-plan]');
      this.planInput = form.querySelector('[data-nlb-plan-input]');
      this.subscribeRadio = form.querySelector('[data-nlb-plan-subscribe]');
      this.intervalWrap = form.querySelector('[data-nlb-interval-wrap]');
      this.interval = form.querySelector('[data-nlb-interval]');
      this.priceTargets = document.querySelectorAll('[data-nlb-price]');

      if (!this.plans.length) return;

      this.onPlanChange = this.onPlanChange.bind(this);
      this.onIntervalChange = this.onIntervalChange.bind(this);

      this.plans.forEach((radio) => radio.addEventListener('change', this.onPlanChange));

      if (this.interval && this.intervalWrap) {
        // Safe to show now that something is listening to it.
        this.intervalWrap.removeAttribute('hidden');
        this.interval.addEventListener('change', this.onIntervalChange);
      }

      // The choice only works with this script running, so it is only offered
      // with this script running.
      if (this.fieldset) this.fieldset.removeAttribute('hidden');

      this.syncPlanInput();
      this.syncPrice();
    }

    onPlanChange() {
      if (this.intervalWrap && this.subscribeRadio) {
        this.intervalWrap.hidden = !this.subscribeRadio.checked;
      }
      this.syncPlanInput();
      this.syncPrice();
    }

    /*
      A one-time purchase means no `selling_plan` field at all. Posting it empty
      is rejected by the cart, so the input is disabled rather than blanked —
      disabled fields are not submitted.
    */
    syncPlanInput() {
      if (!this.planInput) return;
      var choice = this.selected();
      var planId = choice ? choice.value : '';

      this.planInput.value = planId;
      this.planInput.disabled = planId === '';
    }

    // The interval select does not carry a name. It writes the chosen plan id
    // into the subscribe radio, which the hidden input then mirrors — so there
    // is exactly one selling_plan value and no chance of controls disagreeing.
    onIntervalChange() {
      if (!this.subscribeRadio) return;
      var option = this.interval.options[this.interval.selectedIndex];

      this.subscribeRadio.value = this.interval.value;
      if (option && option.dataset.nlbPlanPrice) {
        this.subscribeRadio.dataset.nlbPlanPrice = option.dataset.nlbPlanPrice;
      }

      this.subscribeRadio.checked = true;
      this.syncPlanInput();
      this.syncPrice();
    }

    selected() {
      for (var i = 0; i < this.plans.length; i++) {
        if (this.plans[i].checked) return this.plans[i];
      }
      return null;
    }

    syncPrice() {
      var choice = this.selected();
      if (!choice || !choice.dataset.nlbPlanPrice) return;

      this.priceTargets.forEach(function (target) {
        target.textContent = choice.dataset.nlbPlanPrice;
      });

      document.dispatchEvent(
        new CustomEvent('nlb:price-change', { detail: { price: choice.dataset.nlbPlanPrice } })
      );
    }
  }

  /* ---- Variant radios ---------------------------------------------------- */

  function initVariants(form) {
    var radios = form.querySelectorAll('[data-nlb-variant]');
    var idInput = form.querySelector('[name="id"]');
    if (!radios.length || !idInput) return;

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        idInput.value = radio.value;

        // A variant change changes the price basis, so reload rather than
        // patch: selling-plan allocations differ per variant and guessing at
        // them client-side is how a cart ends up at the wrong price.
        if (radio.dataset.nlbVariantUrl) {
          window.location.href = radio.dataset.nlbVariantUrl;
        }
      });
    });
  }

  /* ---- Sticky add to cart — custom build #2 ------------------------------ */

  class NlbStickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.querySelector('.nlb-product__form form');
      this.buyArea = document.querySelector('.nlb-product__buy');
      this.priceTarget = this.querySelector('[data-nlb-sticky-price]');
      this.qtyInput = this.querySelector('[data-nlb-sticky-quantity]');
      this.mainQty = document.querySelector('[data-nlb-quantity]');

      if (!this.form || !this.buyArea) {
        this.remove();
        return;
      }

      // The button submits the main form by id, so the purchase option, the
      // variant, and the quantity are whatever the real form holds. Nothing is
      // mirrored, so nothing can drift out of sync.
      var submit = this.querySelector('[data-nlb-sticky-submit]');
      if (submit) submit.setAttribute('form', this.form.id);

      this.bindQuantity();
      this.bindPrice();
      this.observe();
    }

    bindQuantity() {
      if (!this.qtyInput || !this.mainQty) return;

      this.qtyInput.value = this.mainQty.value;

      this.querySelectorAll('[data-nlb-sticky-step]').forEach((button) => {
        button.addEventListener('click', () => {
          var step = button.dataset.nlbStickyStep === 'plus' ? 1 : -1;
          var next = Math.max(1, (parseInt(this.mainQty.value, 10) || 1) + step);

          this.mainQty.value = next;
          this.qtyInput.value = next;
          this.mainQty.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      this.mainQty.addEventListener('change', () => {
        this.qtyInput.value = this.mainQty.value;
      });
    }

    bindPrice() {
      if (!this.priceTarget) return;
      document.addEventListener('nlb:price-change', (event) => {
        this.priceTarget.textContent = event.detail.price;
      });
    }

    observe() {
      if (!('IntersectionObserver' in window)) return;

      var observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
          });
        },
        { rootMargin: '0px' }
      );

      observer.observe(this.buyArea);
    }
  }

  /* ---- Boot -------------------------------------------------------------- */

  function init() {
    var form = document.querySelector('.nlb-product__form form');
    if (form) {
      new NlbPurchaseOptions(form);
      initVariants(form);
    }

    if (!customElements.get('nlb-sticky-atc')) {
      customElements.define('nlb-sticky-atc', NlbStickyAtc);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', init);
})();
